"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Lock, Play } from "lucide-react";
import { useDict } from "@/components/i18n/DictProvider";
import { cn } from "@/components/ui/cn";
import { formatDuration, formatEpisodes } from "@/lib/format";
import { type Lang } from "@/lib/i18n";
import { episodeThumb } from "./ottMeta";
import { episodeState } from "./ottEpisodes";
import type { Dict } from "@/lib/i18n";
import type { OttVideo } from "@/lib/lecturehub/types";

/**
 * A series with at least this many episodes leaves the 360px rail for the
 * full-width strip below the player. Four rows fit the rail beside a 16:9
 * player without pushing the access gate under the fold; a fifth does not.
 */
export const EPISODE_STRIP_MIN = 5;

/**
 * Long-series episode browser: the streaming-service pattern (a horizontal,
 * snap-scrolling row of 16:9 episode cards directly under the player) instead
 * of a tall list squeezed into the rail. The rail keeps only the access gate,
 * so the primary CTA stays above the fold however long the series is.
 *
 * - Cards carry the number, the poster (branded play tile when there is none),
 *   the runtime on the thumb, a two-line title and a two-line synopsis.
 * - Desktop gets paging arrows (one viewport of cards per click); touch gets
 *   native swipe with snap points. Both edges fade only while there is more to
 *   scroll in that direction — the fade is a hint, not decoration.
 * - "Show all" swaps the strip for a numbered grid of the same cards, for the
 *   reader who wants to scan the whole run at once.
 *
 * Each card is a button that puts its episode on stage (N01, decision D32);
 * the playing card is marked, and a card's lock or spinner is the episode's
 * OWN state (`episodeState`), like the rail list's.
 */
export function OttEpisodeStrip({
  videos,
  lang,
  cfHash = null,
  selectedIndex = -1,
  onSelect,
}: {
  videos: OttVideo[];
  lang: Lang;
  /** Portal.cloudflareAccountHash — CF-optimized episode posters when present. */
  cfHash?: string | null;
  /** The episode on stage. */
  selectedIndex?: number;
  onSelect?: (index: number) => void;
}) {
  const t = useDict();
  const [expanded, setExpanded] = useState(false);
  const scroller = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ start: el.scrollLeft <= 2, end: el.scrollLeft >= max - 2 });
  }, []);

  useEffect(() => {
    measure();
    const el = scroller.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, expanded]);

  const page = (dir: 1 | -1) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: "smooth" });
  };

  const total = videos.reduce((sum, v) => sum + (v.duration ?? v.video?.duration ?? 0), 0);
  const count = formatEpisodes(videos.length, lang, t.units);
  const meta = total > 0 ? `${count} · ${formatDuration(total)}` : count;

  // Edge fades: only on the side that still has content, so the strip never
  // looks cut off at its actual end.
  const fade = 56;
  const mask = expanded
    ? undefined
    : `linear-gradient(to right, ${edges.start ? "black" : "transparent"}, black ${fade}px, black calc(100% - ${fade}px), ${edges.end ? "black" : "transparent"})`;

  return (
    <section aria-label={t.detail.episodes} className="mt-8">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h2 className="!m-0 font-display !text-[18px] !font-bold !leading-tight !text-foreground md:!text-[20px]">
            {t.detail.episodes}
          </h2>
          <p className="mt-1 text-[12.5px] font-medium tabular-nums text-muted-foreground">
            {meta}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!expanded ? (
            <div className="hidden items-center gap-1.5 md:flex">
              <ArrowButton
                label={t.detail.prevItem}
                disabled={edges.start}
                onClick={() => page(-1)}
              >
                <ChevronLeft className="size-4" aria-hidden />
              </ArrowButton>
              <ArrowButton label={t.detail.nextItem} disabled={edges.end} onClick={() => page(1)}>
                <ChevronRight className="size-4" aria-hidden />
              </ArrowButton>
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="inline-flex h-9 items-center rounded-full border border-border/70 bg-card px-4 font-display text-[11.5px] font-bold uppercase tracking-[0.12em] text-foreground/80 transition-colors hover:border-foreground/30 hover:text-foreground"
          >
            {expanded ? t.detail.showFewerEpisodes : t.detail.showAllEpisodes}
          </button>
        </div>
      </div>

      <ul
        ref={scroller}
        onScroll={expanded ? undefined : measure}
        style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
        className={cn(
          expanded
            ? "grid gap-x-4 gap-y-7 sm:grid-cols-2 lg:grid-cols-4"
            : "flex snap-x snap-mandatory gap-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        )}
      >
        {videos.map((v, i) => (
          <li key={v.id} className={cn(!expanded && "w-[240px] shrink-0 snap-start md:w-[272px]")}>
            <EpisodeCard
              index={i}
              video={v}
              cfHash={cfHash}
              t={t}
              active={i === selectedIndex}
              onSelect={onSelect ? () => onSelect(i) : undefined}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function ArrowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="grid size-9 place-items-center rounded-full border border-border/70 bg-card text-foreground/70 transition-colors hover:border-foreground/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-border/70 disabled:hover:text-foreground/70"
    >
      {children}
    </button>
  );
}

function EpisodeCard({
  index,
  video: v,
  cfHash,
  t,
  active = false,
  onSelect,
}: {
  index: number;
  video: OttVideo;
  cfHash: string | null;
  t: Dict;
  active?: boolean;
  onSelect?: () => void;
}) {
  const duration = v.duration ?? v.video?.duration ?? 0;
  const src = episodeThumb(v, cfHash);
  const state = episodeState(v);
  const stateLabel =
    state === "locked"
      ? t.detail.episodeLocked
      : state === "processing"
        ? t.detail.episodeProcessing
        : state === "unavailable"
          ? t.detail.episodeUnavailable
          : null;
  const chip =
    "rounded-md bg-black/55 px-1.5 py-0.5 text-[11px] tabular-nums text-white backdrop-blur-sm";
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? "true" : undefined}
      className="group block w-full text-left"
    >
      <div
        className={cn(
          "relative aspect-video overflow-hidden rounded-xl bg-gradient-to-br from-teal to-foreground after:pointer-events-none after:absolute after:inset-0 after:rounded-xl after:ring-inset after:content-['']",
          active ? "after:ring-2 after:ring-primary" : "after:ring-1 after:ring-black/10",
        )}
      >
        {src ? (
          // alt="" — the episode title is the h3 under this thumb in the same card.
          <Image
            src={src}
            alt=""
            fill
            sizes="(min-width: 768px) 272px, 240px"
            className="object-cover"
          />
        ) : (
          <span aria-hidden className="absolute inset-0 grid place-items-center text-white/70">
            <Play className="size-6 fill-current" />
          </span>
        )}
        <span className={cn(chip, "absolute left-2.5 top-2.5 font-display font-extrabold")}>
          {String(index + 1).padStart(2, "0")}
        </span>
        {duration > 0 ? (
          <span className={cn(chip, "absolute bottom-2.5 right-2.5 font-medium")}>
            {formatDuration(duration)}
          </span>
        ) : null}
        {state === "locked" || state === "processing" ? (
          <span
            aria-hidden
            className="absolute inset-0 grid place-items-center bg-black/45 text-white"
          >
            {state === "locked" ? (
              <Lock className="size-5" />
            ) : (
              <Loader2 className="size-5 animate-spin" />
            )}
          </span>
        ) : null}
      </div>
      <h3
        className={cn(
          "mt-3 line-clamp-2 font-display !text-[14px] !font-bold !leading-snug transition-colors group-hover:!text-primary",
          active ? "!text-primary" : "!text-foreground",
        )}
      >
        {v.title ?? `${t.detail.episodes} ${index + 1}`}
      </h3>
      {stateLabel ? (
        <p className="mt-1 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {stateLabel}
        </p>
      ) : v.abstract ? (
        <p className="mt-1 line-clamp-2 text-[12.5px] leading-snug text-muted-foreground">
          {v.abstract}
        </p>
      ) : null}
    </button>
  );
}
